import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { api, setSEO } from "@/lib/api";
import VoidAtmosphere from "@/components/VoidAtmosphere";
import ProductCard from "@/components/ProductCard";
import DropListForm from "@/components/DropListForm";

export default function Contraband() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setSEO({
      title: "FEMBOT NEX Contraband | Limited Satirical Activewear",
      description:
        "FEMBOT NEX Contraband: limited-run satirical activewear and dark humor pieces. Final sale, restricted drops. Browse the collection and join the restricted list for preorder access.",
    });
    api.get("/products", { params: { tier: "contraband" } }).then((r) => {
      setProducts(r.data);
    });
  }, []);

  return (
    <div data-testid="contraband-page">
      {/* === HERO === */}
      <section className="relative noise scanlines">
        <VoidAtmosphere intensity="hero" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-1.5 border border-[#ff3b30]/60 bg-[#ff3b30]/10 px-2.5 py-1 font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff3b30]">
              <ShieldAlert size={11} strokeWidth={1.5} />
              Restricted Tier
            </div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#7a6a90]">
              Post Hominem · Final Sale · No Restocks
            </div>
          </div>

          <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#ff1493] mb-3">
            // Classified Collection
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tight">
            <span className="text-[#ff1493]">CONTRA</span>
            <span className="text-white">BAND.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#c8b8e0] leading-relaxed">
            Where the less behaved files go. Dark humor, bad judgment, limited access.
          </p>
        </div>
      </section>

      {/* === PRODUCT GRID — browsable, no gate === */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
              // Restricted Files
            </div>
            <h2 className="mt-1 font-display text-3xl md:text-4xl text-white leading-tight">
              The Collection
            </h2>
          </div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#7a6a90]">
            {products.length} file{products.length === 1 ? "" : "s"} on record
          </div>
        </div>

        {products.length === 0 ? (
          <div
            data-testid="contraband-empty"
            className="border border-[#2a1a3a] bg-[#0a0316]/60 p-8 text-center font-mono text-[12px] tracking-[0.2em] uppercase text-[#7a6a90]"
          >
            // No contraband files currently published.
          </div>
        ) : (
          <div
            data-testid="contraband-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
          >
            {products.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* === FILE NOTICE === */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 pb-4">
        <div className="border border-[#2a1a3a] bg-[#0a0316]/50 p-5">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff1493] flex items-center gap-2">
            <ShieldAlert size={12} strokeWidth={1.5} />
            // File Notice
          </div>
          <p className="mt-2 text-sm md:text-base text-[#c8b8e0] leading-relaxed">
            Contraband pieces are limited-run, satirical, and final sale. Dark humor, bad judgment,
            limited access — buy what fits, regret nothing.
            <br className="hidden sm:block" />
            If you need the clothes to behave,{" "}
            <Link to="/shop" className="text-[#ff1493] hover:underline">
              shop Mainline
            </Link>
            .
          </p>
        </div>
      </section>

      {/* === OPTIONAL RESTRICTED-LIST SIGNUP === */}
      <section className="relative">
        <VoidAtmosphere intensity="subtle" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
                // Restricted List · Optional
              </div>
              <h2 className="mt-2 font-display text-4xl md:text-5xl leading-[0.9] tracking-tight">
                <span className="text-[#ff1493]">JOIN THE</span>
                <br />
                <span className="text-white">CONTRABAND LIST.</span>
              </h2>
              <p className="mt-5 max-w-md text-base text-[#c8b8e0] leading-relaxed">
                Join the Contraband list for preorder access, restricted drop alerts, and bad
                decisions with timestamps. Optional — you can still browse and view every piece
                without signing up.
              </p>
            </div>

            <div className="border border-[#2a1a3a] bg-[#0a0316]/60 p-5 md:p-7">
              <DropListForm
                source="contraband_access"
                testIdPrefix="contraband-list"
                placeholder="Terminal address (email)"
                submitLabel="Join Restricted List"
              />
              <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a6a90] leading-relaxed">
                // No payment. No checkout. Email only — used to transmit drop access when files
                unlock.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
